'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useControlled } from '@mui/material';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Popper from '@mui/material/Popper';
import { styled } from '@mui/material/styles';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { findType } from '../helpers/findChildren';
import { Field } from './Field';
import { Option } from './Option';
import { SelectDropdown } from './SelectDropdown';
import { handleSelectDropdownClose, handleSelectDropdownFocus, handleSelectDropdownKeyDown, handleSelectKeyDown } from './utils/a11y';
import { getSelectedText } from './utils/getSelectedText';
import { useFormReset } from '../utils/useFormReset';
const StyledSelect = styled('select')(({ theme }) => ({
    '&:invalid + [role=combobox]': {
        '--field-border-color': theme.tokens.FuelColorAlertErrorIcon
    }
}));
const StyledPopper = styled(Popper)(({ theme, width }) => ({
    fontWeight: theme.tokens.FuelFontWeightBase,
    fontSize: theme.tokens.FuelFontSizeSm,
    width,
    zIndex: theme.zIndex.modal + 1
}));
const emptyArray = [];
export const Select = forwardRef(({ children, hasDivider, hasSearch, hasSelectAll, multiple, loadingStatus, placeholder, value: userValue, defaultValue: userDefaultValue, onBlur, onBlurCapture, onClose, ...props }, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selected, setSelected] = useControlled({
        controlled: userValue === undefined ? undefined : emptyArray.concat(userValue),
        default: userDefaultValue ? emptyArray.concat(userDefaultValue) : emptyArray,
        name: 'Select',
        state: 'value'
    });
    const fieldRef = useRef(null);
    const selectRef = useRef(null);
    const dispatchChangeEvent = (value) => {
        [...selectRef.current.options].forEach((optionElement) => {
            optionElement.selected = value.includes(optionElement.value);
        });
        selectRef.current?.dispatchEvent(new Event('change', {
            bubbles: true
        }));
    };
    useFormReset(() => {
        setSelected([]);
        dispatchChangeEvent([]);
    }, selectRef);
    useImperativeHandle(ref, () => selectRef.current);
    const dropdownId = `${props.id}-dropdown`;
    const options = findType(Option, children).map((child, childIndex) => ({
        index: childIndex + 1,
        ...child.props
    }));
    const handleClickAway = () => {
        setIsOpen(false);
        onClose?.();
        handleSelectDropdownClose(fieldRef.current);
    };
    const handleOptionClick = (value) => {
        setSelected(value);
        dispatchChangeEvent(value);
        if (!multiple) {
            handleClickAway();
        }
    };
    const handleBlurEvent = (blurHandler) => () => {
        if (selectRef.current && !isOpen) {
            blurHandler?.(Object.defineProperties(new FocusEvent('blur'), {
                currentTarget: {
                    value: selectRef.current,
                    writable: true
                },
                target: {
                    value: selectRef.current,
                    writable: true
                }
            }));
        }
    };
    return (_jsxs(_Fragment, { children: [_jsxs(StyledSelect, { ...props, hidden: true, multiple: multiple, onChange: (event) => props.onChange?.(event), ref: selectRef, value: multiple ? selected : selected[0] || '', children: [_jsx("option", { value: '' }), children] }), _jsx(Field, { "aria-controls": isOpen ? dropdownId : undefined, "aria-expanded": isOpen, "aria-haspopup": 'listbox', disabled: props.disabled, id: `${props.id}-field`, onBlur: handleBlurEvent(onBlur), onBlurCapture: handleBlurEvent(onBlurCapture), onClick: () => {
                    setIsOpen((openState) => !openState);
                }, onKeyDown: handleSelectKeyDown, ref: fieldRef, role: 'combobox', type: 'button', children: getSelectedText({
                    placeholder,
                    selected,
                    options,
                    multiple
                }) }), _jsx(StyledPopper, { anchorEl: fieldRef.current, open: isOpen, placement: 'bottom-start', role: 'presentation', transition: true, width: fieldRef.current?.offsetWidth, children: ({ TransitionProps }) => (_jsx(ClickAwayListener, { onClickAway: handleClickAway, children: _jsx(Grow, { ...TransitionProps, style: {
                            transformOrigin: '0 0'
                        }, children: _jsx(SelectDropdown, { hasDivider: hasDivider, hasSearch: hasSearch, hasSelectAll: hasSelectAll, id: dropdownId, loadingStatus: loadingStatus, multiple: multiple, onFocus: (event) => {
                                handleSelectDropdownFocus(event, fieldRef.current);
                            }, onKeyDown: (event) => {
                                handleSelectDropdownKeyDown(event, handleClickAway);
                            }, onOptionClick: handleOptionClick, options: options, selected: selected }) }) })) })] }));
});
Select.displayName = 'Select';
